// How a settled forecast ends on the timeline.
//
// A resolved forecast ends on a gold outcome mark at the moment it settled, instead of the
// violet resolution-date line: a "Resolved: <outcome>" rule, a short connector from the
// probability trace's last value to where that outcome landed (100% if the traced outcome
// happened, 0% if it did not), and a dimmed canvas past it. The distribution block keeps
// Sayer's own final forecast and marks the outcome that happened, rather than collapsing to
// 100/0.
//
// Everything here is pure: App.jsx and ForecastLane.jsx pass the values they already hold.

import {
  bucketLabel,
  extractDistribution,
  resolvedBucketIndex,
  winnerOf,
} from "../forecast/multiway.js";
import { buildDistributionMoments, leadingLabel, seriesForLabel } from "../forecast/binSeries.js";
import { buildReforecastMoments } from "../forecast/reportPointer.js";

/** Statuses that settle a yes/no question. */
const BINARY_TERMINAL = {
  resolved_yes: "Yes",
  resolved_no: "No",
  resolved_tie: "tie",
};

/**
 * The outcome a settled forecast names, as the label prints it ("Yes", "No", "Carlos
 * Alcaraz", "tie"), or null when nothing names one.
 *
 * Reads the same bucket the distribution block checks off (`resolvedBucketIndex` over the
 * displayed contract), so the timeline label and the gold row cannot disagree.
 *
 * @param {{contract?: any, allMultiway?: boolean, status?: string|null, verdict?: any}} args
 * @returns {string|null}
 */
export function resolvedOutcomeLabel({
  contract,
  allMultiway = false,
  status = null,
  verdict = null,
}) {
  const dist = contract
    ? extractDistribution(contract, { allMultiway, probability: contract.probability })
    : null;
  if (dist && dist.kind !== "binary") {
    const at = resolvedBucketIndex(dist.buckets, dist.kind, status, verdict);
    if (at != null && dist.buckets[at]) return bucketLabel(dist.buckets[at]);
    const named = verdict?.resolved_bucket_label ?? verdict?.resolved_outcome;
    if (typeof named === "string" && named.trim()) return named.trim();
    return status === "resolved_tie" ? "tie" : null;
  }
  return BINARY_TERMINAL[status] ?? null;
}

/**
 * The text of the timeline's resolution label for a settled forecast.
 * @param {string|null} outcome from `resolvedOutcomeLabel`
 * @returns {string}
 */
export function resolvedMarkText(outcome) {
  return outcome ? `Resolved: ${outcome}` : "Resolved";
}

/**
 * The gold resolution mark for a settled forecast, or null while it is open (or scrubbed to a
 * forecast from before it settled).
 *
 * `capMs` is App's trace cap — the moment the probability line stops and the distribution
 * block hangs off: `resolved_at` when it settled before its date, else the resolution date
 * (a resolver that ran late does not move the end of the question). It is null exactly when
 * no terminal verdict is showing, which is what gates the mark.
 *
 * @param {{capMs: number|null, dayToScreen: (d: Date) => number, contract?: any,
 *   allMultiway?: boolean, status?: string|null, verdict?: any}} args
 * @returns {{x: number, ms: number, outcome: string|null, text: string}|null}
 */
export function resolvedMarkOf({ capMs, dayToScreen, contract, allMultiway, status, verdict }) {
  if (capMs == null || !Number.isFinite(capMs)) return null;
  const x = dayToScreen(new Date(capMs));
  if (!Number.isFinite(x)) return null;
  const outcome = resolvedOutcomeLabel({ contract, allMultiway, status, verdict });
  return { x, ms: capMs, outcome, text: resolvedMarkText(outcome) };
}

/**
 * Where the probability trace ends on a settled forecast, and where its outcome landed.
 *
 * Mirrors ProbabilityPlot's own choice of line — the hovered outcome's when a bar is hovered,
 * else the outcome that happened, else the leader; the P(Yes) line when there are no
 * per-outcome series — so the connector always starts on the line actually drawn.
 *
 * `pLast` is that line's value at the resolution moment (`capMs`); `pOutcome` is 1 when the
 * traced outcome happened and 0 when it did not (null when the verdict names no winner, e.g.
 * a tie, so no connector is drawn).
 *
 * @param {{
 *   active: any,
 *   displayContract?: any,
 *   asyncMoments?: Array,
 *   hoveredBinLabel?: string|null,
 *   status?: string|null,
 *   verdict?: any,
 *   capMs: number|null,
 *   allMultiway?: boolean,
 *   perOutcome?: boolean,
 * }} args
 * @returns {{pLast: number, pOutcome: number}|null}
 */
export function resolvedTraceEnd({
  active,
  displayContract = null,
  asyncMoments = [],
  hoveredBinLabel = null,
  status = null,
  verdict = null,
  capMs,
  allMultiway = false,
  perOutcome = true,
}) {
  const contract = active?.contract;
  if (!contract || capMs == null || active?.createdAt == null) return null;
  const dist = extractDistribution(contract, { allMultiway, probability: contract.probability });
  const sel = displayContract ?? contract;
  const selDist = extractDistribution(sel, { allMultiway, probability: sel.probability });
  const resolvedIdx = selDist
    ? resolvedBucketIndex(selDist.buckets, selDist.kind, status, verdict)
    : null;
  const resolvedLabel = resolvedIdx != null ? (selDist.buckets[resolvedIdx]?.label ?? null) : null;
  const hoverIsReal =
    typeof hoveredBinLabel === "string" &&
    Array.isArray(selDist?.buckets) &&
    selDist.buckets.some((b) => b?.label === hoveredBinLabel);
  const binLabel = hoverIsReal
    ? hoveredBinLabel
    : resolvedLabel != null
      ? resolvedLabel
      : leadingLabel(selDist?.buckets);
  const distMoments = perOutcome
    ? buildDistributionMoments({
        history: active.history,
        asyncMoments,
        currentBuckets: dist?.buckets ?? null,
        currentProbability: contract.probability,
        createdAt: active.createdAt,
        allMultiway,
      })
    : [];
  const binMoments = binLabel ? seriesForLabel(distMoments, binLabel) : [];
  const usingBins = binMoments.length > 0;
  const moments = usingBins
    ? binMoments
    : buildReforecastMoments(active.history, asyncMoments, contract.probability, active.createdAt);
  let pLast = null;
  for (const m of moments) {
    if (m.atMs <= capMs && Number.isFinite(m.p)) pLast = m.p;
  }
  if (pLast == null) return null;
  const pOutcome = usingBins
    ? resolvedLabel == null
      ? null
      : binLabel === resolvedLabel
        ? 1
        : 0
    : status === "resolved_yes"
      ? 1
      : status === "resolved_no"
        ? 0
        : null;
  if (pOutcome == null) return null;
  return { pLast, pOutcome };
}

/**
 * The y of probability `p` on the trace, the same mapping ProbabilityPlot and the pie use:
 * 0.5 on the spine, 1 and 0 at the trigger channel's outer edges.
 * @param {number} p
 * @param {number} spineY
 * @param {number} streamHalf
 * @returns {number}
 */
export function traceY(p, spineY, streamHalf) {
  const MAX_TRIGGER_R = 18;
  const channelOffset = streamHalf * 0.55;
  const at1 = spineY - channelOffset - MAX_TRIGGER_R;
  const at0 = spineY + channelOffset + MAX_TRIGGER_R;
  const safeP = Math.max(0, Math.min(1, p));
  return safeP >= 0.5
    ? spineY + (safeP - 0.5) * 2 * (at1 - spineY)
    : spineY + (0.5 - safeP) * 2 * (at0 - spineY);
}

/**
 * A settled distribution with Sayer's own forecast left standing.
 *
 * `applyResolutionToDistribution` collapses the bars to 100/0 on the outcome that happened;
 * the timeline shows the forecast against the outcome instead, so it keeps the verdict
 * (`resolvedIndex`, `badge`, `status`) and restores the forecast's buckets. Both lists are in
 * the same order (the collapse maps over the buckets in place), so the index still points at
 * the right row. A live forecast passes through untouched.
 *
 * @template T
 * @param {{buckets: Array<{label: string, p: number}>}|null} forecast the unresolved distribution
 * @param {T & {buckets: Array, resolvedIndex: number|null, badge: string|null}|null} settled
 * @returns {T|null}
 */
export function withForecastBars(forecast, settled) {
  if (!settled || !forecast) return settled;
  if (settled.resolvedIndex == null && !settled.badge) return settled;
  if (!Array.isArray(forecast.buckets) || forecast.buckets.length !== settled.buckets?.length)
    return settled;
  return { ...settled, buckets: forecast.buckets };
}

/**
 * The resolution marker's hover readout for a settled distribution: the outcome that
 * happened with Sayer's final number for it, so the tooltip reads as forecast vs outcome.
 * Falls back to `winnerOf` for a live forecast.
 * @param {any} dist from `withForecastBars`
 */
export function resolvedReadout(dist) {
  const w = winnerOf(dist);
  if (!w || dist?.resolvedIndex == null) return w;
  return { ...w, badge: "Happened · Sayer's final forecast" };
}
